import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Cookie, X, Shield } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from '../i18n/Link'

interface CookiePreferences {
  necessary: boolean
  analytics: boolean
  marketing: boolean
}

export default function CookieConsent() {
  const { t } = useTranslation('common')
  const [isVisible, setIsVisible] = useState(false)
  const [showPreferences, setShowPreferences] = useState(false)
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  })

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent')
    if (!consent) {
      const timer = setTimeout(() => {
        setIsVisible(true)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [])

  const persist = (prefs: CookiePreferences) => {
    setPreferences(prefs)
    localStorage.setItem('cookieConsent', JSON.stringify(prefs))
    localStorage.setItem('cookieConsentDate', new Date().toISOString())
    setIsVisible(false)
  }
  const acceptAll = () => persist({ necessary: true, analytics: true, marketing: true })
  const acceptSelected = () => persist(preferences)
  const rejectAll = () => persist({ necessary: true, analytics: false, marketing: false })

  const togglePreference = (key: keyof CookiePreferences) => {
    if (key === 'necessary') return
    setPreferences((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  if (!isVisible) return null

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:w-[400px]"
        >
          <div className="cookie-panel glass rounded-2xl p-5 shadow-2xl border border-white/10">
            {!showPreferences ? (
              <div>
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Cookie className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-semibold text-white mb-1">{t('cookie.title')}</h3>
                    <p className="text-gray-400 text-xs leading-relaxed">
                      {t('cookie.text')}
                      <Link to="/cookies" className="text-primary hover:underline ml-1">
                        {t('cookie.more')}
                      </Link>
                    </p>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <button
                    onClick={acceptAll}
                    className="w-full py-2.5 bg-primary hover:bg-primary-dark text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    {t('cookie.acceptAll')}
                  </button>

                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowPreferences(true)}
                      className="flex-1 py-2 bg-white/5 hover:bg-white/10 text-white text-xs rounded-lg transition-colors"
                    >
                      {t('cookie.preferences')}
                    </button>
                    <button
                      onClick={rejectAll}
                      className="flex-1 py-2 bg-white/5 hover:bg-white/10 text-gray-400 text-xs rounded-lg transition-colors"
                    >
                      {t('cookie.reject')}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-primary" />
                    <h3 className="text-base font-semibold text-white">{t('cookie.preferences')}</h3>
                  </div>
                  <button
                    onClick={() => setShowPreferences(false)}
                    className="p-1 text-gray-400 hover:text-white transition-colors"
                    aria-label={t('cookie.close')}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="space-y-3 mb-4">
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div>
                      <span className="text-white text-sm font-medium">{t('cookie.necessary')}</span>
                      <p className="text-gray-500 text-[10px]">{t('cookie.necessaryText')}</p>
                    </div>
                    <div className="w-8 h-4 rounded-full bg-success/30 relative">
                      <div className="absolute right-0.5 top-0.5 w-3 h-3 rounded-full bg-success" />
                    </div>
                  </div>

                  {(['analytics', 'marketing'] as const).map((key) => (
                    <button
                      key={key}
                      onClick={() => togglePreference(key)}
                      className="w-full flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <div className="text-left">
                        <span className="text-white text-sm font-medium">{t(`cookie.${key}`)}</span>
                        <p className="text-gray-500 text-[10px]">{t(`cookie.${key}Text`)}</p>
                      </div>
                      <div
                        className={`w-8 h-4 rounded-full relative transition-colors ${preferences[key] ? 'bg-primary' : 'bg-gray-700'}`}
                      >
                        <div
                          className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${preferences[key] ? 'right-0.5' : 'left-0.5'}`}
                        />
                      </div>
                    </button>
                  ))}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => setShowPreferences(false)}
                    className="flex-1 py-2 bg-white/5 hover:bg-white/10 text-gray-400 text-xs rounded-lg transition-colors"
                  >
                    {t('cookie.back')}
                  </button>
                  <button
                    onClick={acceptSelected}
                    className="flex-1 py-2 bg-primary hover:bg-primary-dark text-white text-xs font-medium rounded-lg transition-colors"
                  >
                    {t('cookie.save')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
